// "Flight details" card of a charter booking: IDs, status, customer, payment method, ad-hoc labels.
import type { ReactNode } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { Button, Card, Descriptions, Space, Tag, Typography } from 'antd';

import type { CharterBookingDetail } from '../../api/types';
import { CHARTER_BOOKING_STATUS_COLORS, CHARTER_BOOKING_STATUS_LABELS, statusLabel } from '../../data/statuses';
import { ADHOC_COLORS, AdhocTag } from '../../components/AdhocTag';
import { AccountDeletedTag } from '../../components/AccountDeletedTag';

const { Text } = Typography;

export function FlightDetailsCard({
  b,
  extra,
  nav,
  dismissMut,
}: {
  b: CharterBookingDetail;
  extra: ReactNode;
  nav: NavigateFunction;
  dismissMut: { isPending: boolean; mutate: (label: 'transfer' | 'passport') => void };
}) {
  return (
    <Card
      title="Flight details"
      extra={extra}
    >
      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
        <Descriptions.Item label="Booking ID"><Text code>{b.reference ?? b.id.slice(-8)}</Text></Descriptions.Item>
        <Descriptions.Item label="Request ID">
          <Button type="link" style={{ padding: 0 }} onClick={() => nav(`/charters/${b.requestId}`)}>
            {b.requestReference}
          </Button>
        </Descriptions.Item>
        <Descriptions.Item label="Flight ID">{b.flightNumber || '—'}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={CHARTER_BOOKING_STATUS_COLORS[b.status]}>
            {statusLabel(CHARTER_BOOKING_STATUS_LABELS, b.status, b.statusLabel)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Customer">
          {b.user?.name || '—'} <AccountDeletedTag at={b.user?.accountDeletedAt} />
        </Descriptions.Item>
        <Descriptions.Item label="Customer email">{b.user?.email || '—'}</Descriptions.Item>
        <Descriptions.Item label="Customer phone">{b.user?.phone || '—'}</Descriptions.Item>
        <Descriptions.Item label="Payment method">
          {b.paymentMethod === 'wire_transfer' ? 'Wire transfer' : b.paymentMethod === 'pre_auth' ? 'Pre-authorisation' : 'Card'}
        </Descriptions.Item>
        {/* SHAR-1028 - ad-hoc labels, removable with a confirmation, the way
            the ShareJet booking screen shows them. */}
        <Descriptions.Item label="Ad-hoc status">
          {b.adhocAwaitingTransfer || b.adhocPassportNeeded ? (
            <Space size={4} wrap>
              {b.adhocAwaitingTransfer ? (
                <AdhocTag
                  color={ADHOC_COLORS.awaitingTransfer}
                  label="Wire transfer required"
                  title="Remove label"
                  description="Remove the “Wire transfer required” label from this booking? The payment stays not received until you mark it as received."
                  loading={dismissMut.isPending}
                  onRemove={() => dismissMut.mutate('transfer')}
                />
              ) : null}
              {/* SHAR-1027 - as on the ShareJet booking details (SHAR-452/453). */}
              {b.adhocPassportNeeded ? (
                <AdhocTag
                  color={ADHOC_COLORS.passportNeededDetail}
                  label="Passport needed"
                  title="Remove label"
                  description="Remove the “Passport needed” label from this booking?"
                  loading={dismissMut.isPending}
                  onRemove={() => dismissMut.mutate('passport')}
                />
              ) : null}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}
